import { useState, type FormEvent } from 'react'
import { DEFAULT_TYPESAFE_MODEL, type TypeSafeCredentialsUpdate, type TypeSafeCredentialsView } from '@rp/contracts'
import { Field } from '../../design'
import { formatDateTime } from '../../lib/time'
import { AI_CREDENTIALS_COPY } from './aiCredentialsCopy'
import { CredentialsFormFooter } from './CredentialsFormFooter'

type TypeSafeErrors = { apiKey?: string; model?: string }

export interface TypeSafeCredentialsFormProps {
  current: TypeSafeCredentialsView | null
  pending: boolean
  error: string | null
  onSubmit: (value: TypeSafeCredentialsUpdate) => void
  onCancel?: () => void
}

const apiKeyHint = (current: TypeSafeCredentialsView | null): string =>
  current === null
    ? AI_CREDENTIALS_COPY.apiKeyNew
    : AI_CREDENTIALS_COPY.apiKeyKept(formatDateTime(new Date(current.secretUpdatedAt)))

const errorsOf = (apiKey: string, model: string, needsKey: boolean): TypeSafeErrors => ({
  ...(needsKey && apiKey === '' ? { apiKey: AI_CREDENTIALS_COPY.invalid.apiKeyRequired } : {}),
  ...(model === '' ? { model: AI_CREDENTIALS_COPY.invalid.model } : {}),
})

const errorProp = (error: string | undefined) => (error === undefined ? {} : { error })

export const TypeSafeCredentialsForm = ({ current, pending, error, onSubmit, onCancel }: TypeSafeCredentialsFormProps) => {
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(current?.model ?? DEFAULT_TYPESAFE_MODEL)
  const [errors, setErrors] = useState<TypeSafeErrors>({})

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const key = apiKey.trim()
    const chosenModel = model.trim()
    const found = errorsOf(key, chosenModel, current === null)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    onSubmit(key === '' ? { model: chosenModel } : { apiKey: key, model: chosenModel })
  }

  return (
    <form className="ai-credentials-form" onSubmit={onFormSubmit} noValidate>
      <Field id="typesafe-key" label={AI_CREDENTIALS_COPY.apiKey} hint={apiKeyHint(current)} {...errorProp(errors.apiKey)}>
        {({ id, describedBy }) => (
          <input
            id={id}
            type="password"
            autoComplete="off"
            aria-describedby={describedBy}
            aria-invalid={errors.apiKey === undefined ? undefined : true}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        )}
      </Field>
      <Field id="typesafe-model" label={AI_CREDENTIALS_COPY.model} {...errorProp(errors.model)}>
        {({ id }) => (
          <input
            id={id}
            className="num"
            autoComplete="off"
            spellCheck={false}
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        )}
      </Field>
      <CredentialsFormFooter pending={pending} error={error} {...(onCancel === undefined ? {} : { onCancel })} />
    </form>
  )
}
